'use client';

import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { formatDate } from 'date-fns';

import {
  StatusSelect,
  useBreadcrumbs,
  useGetSponsoredProducts,
} from '~/entities';
import { SponsoredProductsList } from '~/entities/SponsoredProducts/components/List';
import {
  ContentTitle,
  datePickerSlotProps,
  Pagination,
  SearchBox,
  StyledContainer,
  StyledTableContainer,
} from '~/shared';
import { CampaignStatus } from '~/types';
import { ProductListDTO } from '~/types/product/list.dto';

const PRODUCTS_PAGE_SIZE = 15;
const DEFAULT_SELECTED_STATUS: `${CampaignStatus}`[] = ['ACTIVE'];

export function PatrocinadosView() {
  const breadcrumbs = useBreadcrumbs();
  const session = useSession();
  const manufacturerId = session?.data?.user?.manufacturerId;

  const searchParams = useSearchParams();
  const advertiserSearch = searchParams.get('advertiserUrl') ?? '';

  const [productQuery, setProductQuery] = useState<string>(
    `${advertiserSearch}`
  );

  const [status, setStatus] = useState<`${CampaignStatus}`[]>(
    DEFAULT_SELECTED_STATUS
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [finalDate, setFinalDate] = useState<Date | null>(null);
  const [startDateError, setStartDateError] = useState('');
  const [finalDateError, setFinalDateError] = useState('');
  const [sortByOrderId, setSortByOrderId] = useState<'asc' | 'desc' | false>(
    false
  );
  const [sortByOrderProduct, setSortByOrderProduct] = useState<
    'asc' | 'desc' | false
  >(false);
  const [sortByOrderDevice, setSortByOrderDevice] = useState<
    'asc' | 'desc' | false
  >(false);
  const [sortByOrderInitial, setSortByOrderInitial] = useState<
    'asc' | 'desc' | false
  >(false);
  const [sortByOrderFinish, setSortByOrderFinish] = useState<
    'asc' | 'desc' | false
  >(false);

  const sortConfig = {
    sortByOrderId,
    setSortByOrderId,
    sortByOrderProduct,
    setSortByOrderProduct,
    sortByOrderDevice,
    setSortByOrderDevice,
    sortByOrderInitial,
    setSortByOrderInitial,
    sortByOrderFinish,
    setSortByOrderFinish,
  };

  const [productsFilter, setProductsFilter] = useState<ProductListDTO>({
    query: productQuery,
    count: PRODUCTS_PAGE_SIZE,
    status: status.length > 0 ? status : undefined,
    page: 1,
    manufacturerId,
  });

  const useGetProductsQuery = useGetSponsoredProducts(productsFilter);

  const total = useGetProductsQuery.data?.total ?? 0;
  const totalPages = Math.ceil(total / PRODUCTS_PAGE_SIZE);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current) {
      breadcrumbs.setPaths([
        { label: 'Produtos Patrocinados', path: '/patrocinados' },
      ]);

      useGetProductsQuery.refetch();
      hasInitialized.current = true;
    }
  }, [breadcrumbs, useGetProductsQuery]);

  useDebounce(
    () => {
      setProductsFilter(state => ({
        ...state,
        query: productQuery,
        page: 1,
      }));
    },
    500,
    [productQuery]
  );

  useDebounce(
    () => {
      setProductsFilter(state => ({
        ...state,
        status: status.length > 0 ? status : undefined,
        page: 1,
      }));
    },
    1,
    [status]
  );

  useDebounce(
    () => {
      if (startDate === null) {
        setStartDateError('');
        setProductsFilter(state => ({
          ...state,
          startDate: undefined,
          page: 1,
        }));
        return;
      }

      if (Number.isNaN(startDate.getTime())) {
        setStartDateError('Data invalida');
        return;
      }

      setStartDateError('');
      setProductsFilter(state => ({
        ...state,
        startDate: formatDate(startDate, 'dd-MM-yyyy'),
        page: 1,
      }));
    },
    1,
    [startDate]
  );

  useDebounce(
    () => {
      setFinalDateError('');
      if (finalDate === null) {
        setProductsFilter(state => ({
          ...state,
          finishDate: undefined,
          page: 1,
        }));
        return;
      }

      if (Number.isNaN(finalDate.getTime())) {
        setFinalDateError('Data invalida');
        return;
      }

      setFinalDateError('');
      setProductsFilter(state => ({
        ...state,
        finishDate: formatDate(finalDate, 'dd-MM-yyyy'),
        page: 1,
      }));
    },
    1,
    [finalDate]
  );

  function nextPageHandler() {
    if (productsFilter.page < totalPages) {
      setProductsFilter(state => ({
        ...state,
        page: state.page + 1,
      }));
    }
  }

  function prevPageHandler() {
    if (productsFilter.page > 1) {
      setProductsFilter(state => ({
        ...state,
        page: state.page - 1,
      }));
    }
  }

  useDebounce(
    () => {
      setProductsFilter(state => ({
        ...state,
        orderId: sortByOrderId,
      }));
    },
    1,
    [sortByOrderId]
  );

  useDebounce(
    () => {
      setProductsFilter(state => ({
        ...state,
        orderCampaign: sortByOrderProduct,
      }));
    },
    1,
    [sortByOrderProduct]
  );

  useDebounce(
    () => {
      setProductsFilter(state => ({
        ...state,
        orderDevice: sortByOrderDevice,
      }));
    },
    1,
    [sortByOrderDevice]
  );

  useDebounce(
    () => {
      setProductsFilter(state => ({
        ...state,
        orderInitial: sortByOrderInitial,
      }));
    },
    1,
    [sortByOrderInitial]
  );

  useDebounce(
    () => {
      setProductsFilter(state => ({
        ...state,
        orderFinish: sortByOrderFinish,
      }));
    },
    1,
    [sortByOrderFinish]
  );

  return (
    <>
      <ContentTitle
        title="Produtos Patrocinados"
        isFetching={
          useGetProductsQuery.isLoading || useGetProductsQuery.isFetching
        }
      />

      <StyledContainer>
        <Box
          sx={theme => ({
            display: 'flex',
            gap: 2,
            mb: 4,
            [theme.breakpoints.down('lg')]: { flexDirection: 'column' },
          })}
        >
          <SearchBox
            label="Procurar por ID, Anunciante ou Campanha"
            onChange={setProductQuery}
            value={productQuery}
            fullWidth
          />
          <Box
            sx={theme => ({
              display: 'flex',
              gap: 2,
              width: '100%',
              [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
            })}
          >
            <StatusSelect
              fullWidth
              growAtLg
              selectedStatus={status}
              setter={setStatus}
              multiple
            />

            <DatePicker
              sx={{ width: '100%' }}
              label="Início"
              value={startDate}
              onChange={value => setStartDate(value)}
              slotProps={datePickerSlotProps(startDateError)}
            />

            <DatePicker
              sx={{ width: '100%' }}
              label="Término"
              value={finalDate}
              onChange={value => setFinalDate(value)}
              slotProps={datePickerSlotProps(finalDateError)}
            />
          </Box>
        </Box>

        <StyledTableContainer>
          <SponsoredProductsList
            useGetCampaignsQuery={useGetProductsQuery}
            sortConfig={sortConfig}
          />
        </StyledTableContainer>

        <Pagination
          disabled={
            totalPages === 1 || useGetProductsQuery.isFetching || total === 0
          }
          page={productsFilter.page}
          count={totalPages}
          nextPage={nextPageHandler}
          prevPage={prevPageHandler}
          onChange={(_, newPage) =>
            setProductsFilter(state => ({ ...state, page: newPage }))
          }
        />
      </StyledContainer>
    </>
  );
}
